import { watch } from 'vue';

/*
    Code which handles PLACES (region & location) functionality
*/
export function usePlaces(
    locationsStore,
    locationsQuery,
    paramsFromUrl,
    queryParams,
    regionsStore,
    regionsQuery,
    settings,
    useFetch,
) {

    /*
        Our graphql queries are set up to accept a 'places' param.
        This could either be a region OR a location.
        This function sets the chosen region, if a region id can
        be found in our 'places' param
    */
    function setChosenRegionIfRequired(regions) {

        let chosenPlaces = [];

        // Set chosen places from query params if not already set
        if (paramsFromUrl['places']) {

            chosenPlaces = paramsFromUrl['places'].split(',').map((id) => {
                return parseInt(id);
            });

        }

        if (!chosenPlaces.length || !regions.length) {

            return;

        }

        for (let i = 0; i < chosenPlaces.length; i++) {

            const id = chosenPlaces[i];

            queryParams.pushToParamArray('places', id);

            for (let j = 0; j < regions.length; j++) {

                if (parseInt(regions[j].id) === id) {

                    regionsStore.setChosenRegion(id);

                }

            }

        }

    }

    /*
        Take json from 'regions' graphql query and use it to
        update our regionsStore.

        If the visitor has chosen a particular region, then
        grab that region's locations via a new graphql query

    */
    async function setInitialRegions(json) {

        let regions = json.data.regions;
        let selectedRegion = 0;

        /*
            if admin has selected a region, then remove all other regions
            from the array!
        */
        if (settings.region !== 0) {

            const adminRegion = regions.find((region) => {

                return parseInt(region.id) === parseInt(settings.region);

            });

            regions = [adminRegion];
            selectedRegion = adminRegion;

            // Initial region query param is handled by useParams.getDefaultParams()
        } else {

            await setChosenRegionIfRequired(regions);
            selectedRegion = regionsStore.chosenRegion;

        }

        regionsStore.updateRegions(regions);

        /*
            then grab the locations, and set any
            locations that are in the 'places' query param
        */
        if (selectedRegion) {

            useFetch({
                url: settings['host'],
                apiKey: settings['key'],
                query: locationsQuery({regionId: regionsStore.chosenRegion}),
                callback: setInitialLocations,
            });

        }
    }

    /*
        Take json returned from a graphql query and
        simplify it into an array of objects, named
        'locationsArray', which is structured as follows:

        [
            {
                id: int
                title: string
                checked: bool
            },
            etc.
        ]

        This makes it easier to manipulate the data in the app
    */

    async function createLocationsArray(locations) {

        let locationsArray = [];

        if (locations.length) {

            for (let i = 0; i < locations.length; i++) {

                const location = locations[i];

                locationsArray.push(
                    {
                        id: location.id,
                        name: location.name,
                        checked: false,
                    }
                )

                if (i === locations.length - 1) {

                    return locationsArray;

                }

            }

        }

        return locationsArray;

    }

    /*
        Our graphql queries are set up to accept a 'places' param.
        This could either be a region OR a location.
        This function sets the chosen locations, if at least one
        location id can be found in our 'places' param
    */
    async function setChosenLocationsIfRequired(allLocations) {

        let chosenPlaceIDs = [];
        let selectedLocations = [];

        // Set chosen places from query params if not already set
        if (paramsFromUrl['places']) {

            // eslint-disable-next-line no-param-reassign
            chosenPlaceIDs = paramsFromUrl['places'].split(',').map((id) => {
                return parseInt(id);
            });;

        }

        if (!chosenPlaceIDs.length || !allLocations.length) {

            return;

        }

        // Loop through all chosen places
        const chosenRegionId = parseInt(regionsStore.chosenRegion);
        for (let i = 0; i < chosenPlaceIDs.length; i++) {

            const id = chosenPlaceIDs[i];

            /*
                If the chosen place is not the chosen region,
                and it's not already in the 'places' query param,
                then add it to the 'places' query param
            */
            if (
                id !== chosenRegionId
                && queryParams.params.places.indexOf(id) === -1
            ) {

                queryParams.pushToParamArray('places', id);

            }

            // Loop through all locations
            for (let j = 0; j < allLocations.length; j++) {

                // If the location id matches the chosen place id then set it to checked
                if (parseInt(allLocations[j].id) === id) {

                    allLocations[j].checked = true;
                    selectedLocations.push(allLocations[j]);

                }

            }

        }

        // Update the locations store
        locationsStore.updateLocations(allLocations);
        locationsStore.updateSelectedLocations(selectedLocations);

    }

    /*
        set initially chosen locations,
        with data returned from a graphql call
    */
    async function setInitialLocations(json) {

        let locations = json.data.locations;

        const allLocations = await createLocationsArray(locations);

        /*
            if admin has selected a location, then remove all other locations
            from the array!
        */
        if (settings.locations.length) {

            const adminLocations = allLocations.filter((location) => {

                return settings.locations.includes(parseInt(location.id));

            });

            // // Set the admin location to checked
            // adminLocation.checked = true;
            adminLocations.forEach((location) => {

                // Set the admin location to checked
                location.checked = true;

            })

            // // Update the location filters
            locationsStore.updateLocations(adminLocations);

            // // Update the selected locations
            await setChosenLocationsIfRequired(adminLocations);

            // Initial location query param is handled by useParams.getDefaultParams()

        } else {

            locationsStore.updateLocations(allLocations);

            await setChosenLocationsIfRequired(allLocations);

        }

    }

    /*
        Initial setup: this is the function that gets all regions
    */
    async function getPlaces() {

        // show the filters on the front end
        if (settings.hideRegionFilters === 0) {

            if (!settings.showOnlyLocationsFilter) {

                regionsStore.setShowRegions(true);

            }

            locationsStore.setShowLocations(true);

        }

        let query = regionsQuery();
        let callback = setInitialRegions;
        if (settings.showOnlyLocationsFilter) {
            let params = {};
            if (settings.region !== 0) {
                params = {regionId: settings.region};
            }
            query = locationsQuery(params);
            callback = setInitialLocations;
        }

        useFetch({
            url: settings['host'],
            apiKey: settings['key'],
            query,
            callback,
        });

    }

    /*
        Watch function which provides continued functionality
        after the initial... initialisation.
    */
    function watchPlaces(){

        /*
            every time we choose a new region:
                1. reset the 'places' query param!
                2. grab new locations for the locations store!
        */
        watch(regionsStore, () => {

            /*
                If there is no region selected (in cms or front end) and showOnlyLocationsFilter is not checked in cms
                reset locations
             */
            if (!regionsStore.chosenRegion) {

                queryParams.resetParamArray('places');

                if (!settings.showOnlyLocationsFilter && !settings.region) {

                    locationsStore.updateLocations([]);

                }

                return;

            }

            queryParams.resetParamArray(
                'places',
                parseInt(regionsStore.chosenRegion)
            );

            useFetch({
                url: settings['host'],
                apiKey: settings['key'],
                query: locationsQuery({regionId: regionsStore.chosenRegion}),
                callback: (json) => {
                    locationsStore.updateLocations(json);
                    locationsStore.updateSelectedLocations([]);
                },
            });

        });

        /*
            every time we choose a new location,
            update the 'places' query param!
        */
        watch(locationsStore, () => {

            const locations = locationsStore.selectedLocations;

            queryParams.resetParamArray(
                'places',
            );

            if (locations.length) {

                for (let i = 0; i < locations.length; i++) {

                    queryParams.pushToParamArray('places', parseInt(locations[i].id));

                }
            }

            if (regionsStore.chosenRegion) {

                queryParams.pushToParamArray('places', parseInt(regionsStore.chosenRegion));

            }


            /*
                If there are no region or locations selected reset the places param
             */
            if (!regionsStore.chosenRegion && !locationsStore.selectedLocations.length) {

                queryParams.resetParamArray(
                    'places',
                );

            }

        });

    }

    return {
        getPlaces,
        watchPlaces,
    }
}
